import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { PersistentNotification } from '@components/base-persistent-notification';
import { CurrentUser } from '@models/user';
import {
  AcceptTermsOfServiceInput,
  AcceptTermsOfServiceMutation,
  AddressCourseRequirement,
  ChangePasswordInput,
  ChangePasswordMutation,
  ClearImpersonationMutation,
  ConfirmContactMethodInput,
  ConfirmContactMethodMutation,
  CourseRequirement,
  CourseRequirementsTypeEnum,
  CustomField,
  CustomFieldCourseRequirement,
  EmailCourseRequirement,
  HandleInviteInput,
  HandleInviteMutation,
  LoadCurrentUserQuery,
  LoadCurrentUserQueryResult,
  MakeTrainingManagerInput,
  MakeTrainingManagerMutation,
  ResendConfirmationCodeInput,
  ResendConfirmationCodeMutation,
  UpdateUserInput,
  UpdateUserMutation,
  UseActivationCodeInput,
  UseActivationCodeMutation,
  UseEnrolmentKeyInput,
  UseEnrolmentKeyMutation,
  User,
  UserPermissions
} from '@queries/generated';
import { castAs, finalMap } from '@rx/final-map';
import { customFieldRequired } from '@util/custom-fields-required';
import { map, pluck, shareReplay, switchMap, take } from 'rxjs/operators';

import { RecursivePartial } from '@models/apollo';
import { AddressService } from './address.service';
import { UsersService } from './users.service';

function isCustomFieldRequirement(
  courseRequirement: Pick<CourseRequirement, 'type'>
): courseRequirement is CustomFieldCourseRequirement {
  return courseRequirement.type === CourseRequirementsTypeEnum.CustomFieldCourseRequirement;
}

function isEmailRequirement(
  courseRequirement: Pick<CourseRequirement, 'type'>
): courseRequirement is EmailCourseRequirement {
  return courseRequirement.type === CourseRequirementsTypeEnum.EmailCourseRequirement;
}

function isAddressRequirement(
  courseRequirement: Pick<CourseRequirement, 'type'>
): courseRequirement is AddressCourseRequirement {
  return courseRequirement.type === CourseRequirementsTypeEnum.AddressCourseRequirement;
}

@Injectable({
  providedIn: 'root'
})
export class CurrentUserService {
  private query = this.loadCurrentUser.watch();

  user$ = this.query.valueChanges.pipe(
    map(({ data }) => new CurrentUser(data.viewer)),
    shareReplay(1)
  );
  memories$ = this.user$.pipe(pluck('memories'));
  allowVenomOptOut$ = this.user$.pipe(map(({ allowVenomOptOut }) => allowVenomOptOut));
  locale$ = this.user$.pipe(map(({ locale }) => locale));
  locales$ = this.query.valueChanges.pipe(map(({ data: { locales } }) => locales));
  currencies$ = this.query.valueChanges.pipe(map(({ data: { viewer } }) => viewer.currencies));
  timeZones$ = this.query.valueChanges.pipe(
    map(({ data: { timeZones } }) => timeZones.map((zone) => ({ label: zone, value: zone })))
  );
  pendingTermsOfServices$ = this.user$.pipe(map(({ pendingTermsOfServices }) => pendingTermsOfServices));

  notifications$ = this.query.valueChanges.pipe(
    map(({ data: { viewer, currentStorefront } }) => {
      const { impersonatedBy, contactMethods, receivedInvites } = viewer;
      const notifications: PersistentNotification[] = [];
      const requiredFieldsNotifications = this.requiredFieldsNotifications(viewer, currentStorefront);
      const requiredTMNotificaitons = this.requiredTMNotifications(viewer);

      if (requiredTMNotificaitons.length) {
        requiredTMNotificaitons.forEach((notification) => notifications.push(notification));
        this.router.navigate(['training', 'settings']);
      }

      if (requiredFieldsNotifications.length) {
        requiredFieldsNotifications.forEach((notification) => notifications.push(notification));
        this.router.navigate(['user', 'account']);
      }

      return notifications.concat(
        contactMethods.filter((method) => !method.confirmed),
        receivedInvites.filter((invite) => !invite.handled),
        [impersonatedBy].filter((impersonator) => impersonator)
      );
    })
  );

  storefront$ = this.query.valueChanges.pipe(map(({ data: { currentStorefront } }) => currentStorefront));

  constructor(
    private loadCurrentUser: LoadCurrentUserQuery,
    private usersService: UsersService,
    private router: Router,
    private addressService: AddressService,
    private changePasswordMutation: ChangePasswordMutation,
    private confirmContactMethodMutation: ConfirmContactMethodMutation,
    private handleInviteMutation: HandleInviteMutation,
    private resendConfirmationCodeMutation: ResendConfirmationCodeMutation,
    private updateUserMutation: UpdateUserMutation,
    private useEnrolmentKeyMutation: UseEnrolmentKeyMutation,
    private useActivationCodeMutation: UseActivationCodeMutation,
    private clearImpersonationMutation: ClearImpersonationMutation,
    private acceptTermsOfServiceMutation: AcceptTermsOfServiceMutation,
    private makeTrainingManagerMutation: MakeTrainingManagerMutation
  ) {}

  memory(name: string) {
    return this.memories$.pipe(map((memories) => memories.find((memory) => memory.name === name)));
  }

  refresh() {
    return this.query.refetch();
  }

  can<T extends keyof UserPermissions>(permission: T) {
    return this.user$.pipe(map((user) => user.can[permission]));
  }

  changePassword(input: ChangePasswordInput) {
    return this.changePasswordMutation.mutate({ input }).pipe(
      map(({ data }) => data.changePassword),
      finalMap('user')
    );
  }

  confirmContactMethod(input: ConfirmContactMethodInput) {
    return this.confirmContactMethodMutation.mutate({ input }).pipe(
      map(({ data }) => data.confirmContactMethod),
      finalMap('contactMethod')
    );
  }

  handleInvite(input: HandleInviteInput) {
    return this.handleInviteMutation.mutate({ input }).pipe(
      map(({ data }) => data.handleInvite),
      finalMap('invite')
    );
  }

  loadUser() {
    return this.query.valueChanges.pipe(map(({ data }) => data as Required<LoadCurrentUserQueryResult>));
  }

  resendConfirmationCode(input: ResendConfirmationCodeInput) {
    return this.resendConfirmationCodeMutation
      .mutate({ input })
      .pipe(map(({ data }) => data.resendConfirmationCode.ok));
  }

  updateUser(input: UpdateUserInput) {
    return this.updateUserMutation
      .mutate(
        { input },
        {
          refetchQueries: ['ProvideSupport']
        }
      )
      .pipe(
        map(({ data }) => data.updateUser),
        finalMap('user'),
        castAs<User>()
      );
  }

  useEnrolmentKey(input: UseEnrolmentKeyInput) {
    return this.useEnrolmentKeyMutation
      .mutate({ input }, { refetchQueries: ['LoadLearnerCourseList', 'LoadCurrentUser'] })
      .pipe(
        map(({ data }) => data.useEnrolmentKey),
        map(({ errors, grant }) => {
          if (errors?.length) {
            throw errors;
          }
          return grant;
        })
      );
  }

  useActivationCode(input: UseActivationCodeInput) {
    return this.useActivationCodeMutation.mutate({ input }).pipe(
      map(({ data }) => data.useActivationCode),
      finalMap('user')
    );
  }

  clearImpersonation() {
    return this.clearImpersonationMutation.mutate().pipe(
      map(({ data }) => {
        const clear = data.clearImpersonation;
        if (clear.success) {
          return new URL(clear.url, location.origin);
        }
      })
    );
  }

  acceptTermsOfService(input: AcceptTermsOfServiceInput) {
    return this.acceptTermsOfServiceMutation.mutate({ input }).pipe(map(({ data }) => data.acceptTermsOfService));
  }

  makeTrainingManager(input: MakeTrainingManagerInput) {
    return this.makeTrainingManagerMutation.mutate({ input }).pipe(
      map(({ data }) => data.makeTrainingManager),
      finalMap('user')
    );
  }

  remember(name: string, value: string) {
    return this.user$.pipe(
      map(({ gid: userId }) => {
        return {
          userId,
          userMemory: {
            name,
            value
          }
        };
      }),
      take(1),
      switchMap(this.usersService.remember)
    );
  }

  // TODO: After accepting an invite, refetch the viewer
  /**
   * Collect all notifications which instruct the user to add information to their account information:
   *
   * These are persistent notifications which state the field(s) the user is required to complete and
   * mention the storefront or the course which is requiring the user to provide that information.
   */
  private requiredFieldsNotifications(
    viewer: LoadCurrentUserQueryResult['viewer'],
    currentStorefront: LoadCurrentUserQueryResult['currentStorefront']
  ): PersistentNotification[] {
    const { courseRequirements, customValues, address, email } = viewer;
    const { addressRequired, customFields } = currentStorefront;
    const courseRequiredCustomFields: CustomField[] = [];
    let courseRequiredAddress = false;
    const addressCompleted = this.addressService.hasExpectedValues(address);
    const collatedNotifications = { storefrontRequired: [], courseRequired: [] };
    for (const courseRequirement of courseRequirements) {
      if (
        isCustomFieldRequirement(courseRequirement) &&
        courseRequirement.customFields.some((customField) => customFieldRequired(customField, customValues, true))
      ) {
        for (const customField of courseRequirement.customFields) {
          courseRequiredCustomFields.push(customField);
          collatedNotifications.courseRequired.push({
            __typename: 'FieldsRequired',
            meta: { customField, course: courseRequirement.course }
          });
        }
      }
      if (isEmailRequirement(courseRequirement) && !email) {
        collatedNotifications.courseRequired.push({
          __typename: 'FieldsRequired',
          meta: { email: true, course: courseRequirement.course }
        });
      }
      if (isAddressRequirement(courseRequirement) && !addressCompleted) {
        courseRequiredAddress = true;
        collatedNotifications.courseRequired.push({
          __typename: 'FieldsRequired',
          meta: { address: true, course: courseRequirement.course }
        });
      }
    }

    function isNotAlreadyRequired(customField: Pick<CustomField, 'id'>) {
      const alreadyRequired = courseRequiredCustomFields.filter(
        (alreadyRequiredField) => alreadyRequiredField.id === customField.id
      );
      return alreadyRequired.length === 0;
    }

    for (const field of customFields) {
      if (customFieldRequired(field, customValues) && isNotAlreadyRequired(field)) {
        collatedNotifications.storefrontRequired.push({
          __typename: 'FieldsRequired',
          name: currentStorefront.name,
          meta: { customField: field }
        });
      }
    }
    if (!courseRequiredAddress && addressRequired && !addressCompleted) {
      collatedNotifications.storefrontRequired.push({
        __typename: 'FieldsRequired',
        name: currentStorefront.name,
        meta: { address: true }
      });
    }

    return collatedNotifications.storefrontRequired.concat(collatedNotifications.courseRequired);
  }

  private requiredTMNotifications(
    viewer: RecursivePartial<Pick<User, 'roles' | 'certificationDefault'>>
  ): PersistentNotification[] {
    let notifications = [];
    if (viewer.roles.includes('training_manager') && viewer.certificationDefault === null) {
      notifications.push({
        __typename: 'FieldsRequired',
        meta: { certificationDefault: true }
      });
    }

    return notifications;
  }
}
